import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import {
  VariantFulfillmentMode,
  VariantStatus,
  type Product,
  type Variant,
} from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb } from '@/features/media';

import { productKeys } from '../hooks/product-keys';
import { useDefaultWeightUnit } from '../hooks/use-default-weight-unit';
import { useProductMedia, useSetProductMedia } from '../hooks/use-product-media';
import type { VariantFormInput, VariantFormOutput } from '../schemas/variant-form-schema';

import { VariantImageDialog } from './variant-image-dialog';
import { WeightInput } from './weight-input';

const INHERIT = '__inherit__';
const INLINE_IMAGES = 5;

function useVariantFormContext() {
  return useFormContext<VariantFormInput, unknown, VariantFormOutput>();
}

/**
 * The product's images beside the variant: one click links an image, straight away like the
 * dialog behind the last tile, which holds the rest of a long gallery and "No image".
 */
export function VariantImagePicker({ product, variant }: { product: Product; variant: Variant }) {
  const { t } = useTranslation('products');
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const queryClient = useQueryClient();
  const media = useProductMedia(product.id);
  const save = useSetProductMedia(product.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const items = media.data ?? [];
  const current = items.find((item) => item.variantIds.includes(variant.id));
  const first = items.slice(0, INLINE_IMAGES);
  const shown = current && !first.includes(current) ? [...first.slice(0, -1), current] : first;

  const pick = (mediaId: string) =>
    save.mutate(
      {
        items: items.map((item) => ({
          mediaId: item.mediaId,
          variantIds: [
            ...item.variantIds.filter((id) => id !== variant.id),
            ...(item.mediaId === mediaId ? [variant.id] : []),
          ],
        })),
        featuredMediaId: product.featuredMediaId,
      },
      {
        // A variant's `mediaId` is read from these links.
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: productKeys.variants(product.id) });
        },
      },
    );

  return (
    <div className="flex flex-wrap gap-2">
      {shown.map((item, position) => (
        <button
          key={item.mediaId}
          type="button"
          aria-label={t('variantPage2.useImage', { position: position + 1 })}
          aria-pressed={item === current}
          disabled={!canWrite || save.isPending}
          onClick={() => {
            if (item !== current) pick(item.mediaId);
          }}
          className={cn(
            'size-11 overflow-hidden rounded-lg border-2',
            item === current ? 'border-primary' : 'border-border',
          )}
        >
          <MediaThumb media={item.media} className="size-full rounded-none" />
        </button>
      ))}
      {canWrite ? (
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          aria-label={t('variantPage.image.change')}
          className="hover:border-primary text-muted-foreground flex size-11 items-center justify-center rounded-lg border border-dashed"
        >
          <ImagePlus aria-hidden className="size-4" />
        </button>
      ) : null}
      {dialogOpen ? (
        <VariantImageDialog
          product={product}
          variant={variant}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      ) : null}
    </div>
  );
}

export function VariantStatusField() {
  const { t } = useTranslation(['products', 'common']);
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name="status"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('products:variants.columns.status')}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} disabled={field.disabled}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {Object.values(VariantStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`common:status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormItem>
      )}
    />
  );
}

/** What makes this the variant it is: read-only, a different combination is a new variant. */
export function VariantOptionValues({ product, variant }: { product: Product; variant: Variant }) {
  return product.options.map((option) => {
    const code = variant.optionValues[option.key];
    const value = option.values.find((v) => v.value === code);
    return (
      <div key={option.key} className="grid gap-1.5">
        <span className="text-sm font-medium">{option.name}</span>
        <span className="text-sm">{value?.label ?? code ?? '—'}</span>
      </div>
    );
  });
}

export function VariantMoneyField({
  name,
  label,
  currency,
  hint,
}: {
  name: 'price' | 'compareAt' | 'cost';
  label: string;
  currency: string;
  hint?: string;
}) {
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <MoneyInput
              currency={currency}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={field.disabled}
              aria-invalid={fieldState.invalid}
            />
          </FormControl>
          {hint ? <FormDescription>{hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function VariantTextField({
  name,
  label,
  extra,
}: {
  name: 'sku' | 'barcode' | 'taxCode';
  label: string;
  extra?: ReactNode;
}) {
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{label}</FormLabel>
            {extra}
          </div>
          <FormControl>
            <Input
              {...field}
              spellCheck={false}
              className={name === 'taxCode' ? '' : 'font-mono'}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function VariantNumberField({
  name,
  label,
  subLabel = false,
}: {
  name: 'lengthMm' | 'widthMm' | 'heightMm' | 'leadTimeDaysMin' | 'leadTimeDaysMax';
  label: string;
  subLabel?: boolean;
}) {
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex-1">
          <FormLabel className={subLabel ? 'text-muted-foreground text-xs font-normal' : undefined}>
            {label}
          </FormLabel>
          <FormControl>
            <Input {...field} inputMode="decimal" className="tabular-nums" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

type VariantSwitchName = 'taxable' | 'allowBackorder' | 'trackInventory' | 'requiresShipping';

/** A switch in a card's field grid. */
export function VariantToggleField({
  name,
  label,
  hint,
  disabled = false,
}: {
  name: VariantSwitchName;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-start gap-2 self-center">
          <FormControl>
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={(field.disabled ?? false) || disabled}
            />
          </FormControl>
          <div className="grid gap-0.5">
            <FormLabel className="font-normal">{label}</FormLabel>
            {hint ? <FormDescription>{hint}</FormDescription> : null}
          </div>
        </FormItem>
      )}
    />
  );
}

/** Sell when out of stock, which only means something while stock is tracked. */
export function VariantBackorderField() {
  const { t } = useTranslation('products');
  const { control } = useVariantFormContext();
  const trackInventory = useWatch({ control, name: 'trackInventory' });
  return (
    <VariantToggleField
      name="allowBackorder"
      label={t('variantPage.sellWhenOut')}
      hint={t('variants.sheet.allowBackorderHint')}
      disabled={!trackInventory}
    />
  );
}

/** A switch in a card's title row. */
export function VariantHeaderSwitch({ name, label }: { name: VariantSwitchName; label: string }) {
  const { control } = useVariantFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center gap-2">
          <FormControl>
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={field.disabled}
            />
          </FormControl>
          <FormLabel className="text-[13px] font-normal">{label}</FormLabel>
        </FormItem>
      )}
    />
  );
}

/** Weight and packed size, or why there are none while the variant isn't physical. */
export function VariantShippingFields() {
  const { t } = useTranslation('products');
  const { control } = useVariantFormContext();
  const defaultUnit = useDefaultWeightUnit();
  const requiresShipping = useWatch({ control, name: 'requiresShipping' });
  if (!requiresShipping) {
    return <p className="text-muted-foreground text-sm">{t('variantPage.notPhysical')}</p>;
  }
  return (
    <div className="grid items-start gap-4 sm:grid-cols-2">
      <FormField
        control={control}
        name="weight"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('variants.columns.weight')}</FormLabel>
            <WeightInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              defaultUnit={defaultUnit}
              disabled={field.disabled}
              aria-invalid={fieldState.invalid}
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid gap-1.5">
        <Label>{t('variantPage.packedSize')}</Label>
        <div className="flex items-start gap-2">
          <VariantNumberField name="lengthMm" label={t('variantPage.length')} subLabel />
          <span className="text-muted-foreground mt-7">×</span>
          <VariantNumberField name="widthMm" label={t('variantPage.width')} subLabel />
          <span className="text-muted-foreground mt-7">×</span>
          <VariantNumberField name="heightMm" label={t('variantPage.height')} subLabel />
        </div>
      </div>
    </div>
  );
}

export function VariantFulfilmentFields() {
  const { t } = useTranslation('products');
  const { control } = useVariantFormContext();
  return (
    <>
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name="fulfillmentMode"
          render={({ field }) => (
            <FormItem className="sm:col-span-2 sm:max-w-[calc(50%-0.5rem)]">
              <FormLabel>{t('variantPage2.fulfilment')}</FormLabel>
              <Select
                value={field.value || INHERIT}
                onValueChange={(value) => field.onChange(value === INHERIT ? '' : value)}
                disabled={field.disabled}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={INHERIT}>{t('variantPage2.sameAsProduct')}</SelectItem>
                  {Object.values(VariantFulfillmentMode).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {t(`fulfillmentMode.${mode}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        <VariantNumberField name="leadTimeDaysMin" label={t('variantPage2.productionFrom')} />
        <VariantNumberField name="leadTimeDaysMax" label={t('variantPage2.productionTo')} />
      </div>
      <p className="text-muted-foreground text-xs">{t('variants.sheet.leadTimeHint')}</p>
    </>
  );
}

/**
 * Delete variant with its confirmation. The host runs the delete: the row leaving the cache
 * unmounts this, so a callback held here would never fire.
 */
export function VariantDeleteButton({
  variant,
  description,
  onDelete,
}: {
  variant: Pick<Variant, 'id' | 'title'>;
  /** What the delete reaches; defaults to the catalogue wording. */
  description?: string;
  onDelete: (variantId: string) => void;
}) {
  const { t } = useTranslation(['products', 'common']);
  return (
    <Can permission="products.write">
      <div className="flex justify-start">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 aria-hidden />
              {t('products:variants.actions.delete')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('products:variants.delete.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {description ??
                  t('products:variants.delete.description', { variant: variant.title })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(variant.id)}>
                {t('common:actions.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Can>
  );
}
